import { useState } from 'react';

import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next/pages';

import { Button, Col, Form, Input, InputNumber, message, Row, Select } from 'antd';
import { DefaultOptionType } from 'antd/es/select';

import { getTemperatures } from '@/api/reference-data';
import { Container, customizeRequiredMark } from '@/components/common';
import { LocationSelect } from '@/components/LocationSelect/LocationSelect';
import { encodedAndFilterValues } from '@/components/PersonalAccountWarehouses/FindWarehouse/SearchForm/utils';
import { MainWarehousesFiltersForm } from '@/components/SearchWarehouses/SearchWarehouses';
import { STORAGE_SEARCH_URL } from '@/constants/routes';
import useAsyncEffect from '@/hooks/use-async-effect';
import { TranslationNamespace } from '@/i18n';
import { TemperatureRegime } from '@/types/temperature-regime';

import fishIcon from './assets/fishIcon.svg';
import picture from './assets/picture.webp';

import cls from './WarehouseSearch.module.scss';

export default function WarehouseSearch() {
	const [form] = Form.useForm<MainWarehousesFiltersForm>();
	const [temperatures, setTemperatures] = useState<TemperatureRegime[]>([]);
	const router = useRouter();
	const t = useTranslation(TranslationNamespace.Marketing).t;

	useAsyncEffect(async () => {
		try {
			const response = (await getTemperatures(false)) as TemperatureRegime[];
			setTemperatures(response);
		} catch {
			message.error(t('Ошибка при получении температур'));
		}
	}, []);

	const submitHandler = async (values: MainWarehousesFiltersForm) => {
		await router.push({
			pathname: STORAGE_SEARCH_URL,
			query: encodedAndFilterValues(values),
		});
	};

	return (
		<section className={cls.section}>
			<div className={cls.picture} style={{ backgroundImage: `url(${picture.src})` }} />

			<Container className={cls.container}>
				<div className={cls.titleBlock}>
					<h2 className={cls.title}>
						{t(
							'main-landing.warehouse-search.find-the-best-warehouse-for-fish-and-seafood-storage-across-russia',
						)}
					</h2>
					<Button
						className={cls.seeOnMapButton}
						type="primary"
						href={`${STORAGE_SEARCH_URL}?map=open`}
					>
						{t('main-landing.warehouse-search.view-on-map')}
					</Button>
				</div>
				<div className={cls.main}>
					<ul className={cls.fishIconsList}>
						{[...Array(8)].map((_, index) => (
							<li
								key={index}
								className={cls.fishIconItem}
								style={{ backgroundImage: `url(${fishIcon.src})` }}
							/>
						))}
					</ul>
					<Form<MainWarehousesFiltersForm>
						form={form}
						layout="vertical"
						className={cls.card}
						onFinish={submitHandler}
						requiredMark={customizeRequiredMark}
					>
						<p className={cls.cardTitle}>{t('Поиск склада')}</p>
						<Row gutter={[24, 0]} className={cls.formGrid}>
							<Col span={24}>
								<Form.Item label={t('Местоположение')} name="location">
									<LocationSelect
										placeholder={t('Введите регион')}
										inputArea="input"
										virtual={false}
										toBound="city"
										fromBound="city"
										valueField="value"
										onChange={(_, option) => {
											const locationOption = option as DefaultOptionType | undefined;
											form.setFieldsValue({
												longitude: locationOption?.additionalInfo?.geo_lon,
												latitude: locationOption?.additionalInfo?.geo_lat,
											});
										}}
									/>
								</Form.Item>
							</Col>
							<Form.Item name="latitude" noStyle>
								<Input type="hidden" />
							</Form.Item>
							<Form.Item name="longitude" noStyle>
								<Input type="hidden" />
							</Form.Item>
							<Col xs={24} sm={12} lg={24} xl={12}>
								<Form.Item label={t('Радиус поиска, км')} name="max_distance">
									<InputNumber
										controls={false}
										className={cls.formControl}
										placeholder="500"
										size="large"
										suffix="км"
									/>
								</Form.Item>
							</Col>
							<Col xs={24} sm={12} lg={24} xl={12}>
								<Form.Item label={t('Температурный режим')} name="temp_regime">
									<Select
										className={cls.formControl}
										placeholder="-18° C"
										size="large"
										options={temperatures
											.slice()
											.sort((a, b) => a.degree - b.degree)
											.map(({ id, degree }) => ({ label: `${degree}° C`, value: id }))}
									/>
								</Form.Item>
							</Col>
							<Col span={24}>
								<Form.Item label={t('Масса брутто, кг')} name="free_volume">
									<InputNumber
										controls={false}
										className={cls.formControl}
										placeholder="25000"
										size="large"
										suffix="кг"
									/>
								</Form.Item>
							</Col>
						</Row>
						<Button htmlType="submit" size="large" type="primary" className={cls.submitButton}>
							{t('Подобрать склад')}
						</Button>
					</Form>
				</div>
			</Container>
		</section>
	);
}
